/**
 * Swedish/English texts for invoice batch dry-run ("Testkör") findings and
 * "kunde inte kontrolleras" items, keyed by the stable `code` the invoice
 * system services (oo-visma-net-service, oo-fortnox-service) report.
 *
 * To support a new code: add one entry below. `{{name}}` is replaced with
 * `args.name` from the finding. A text can be a list of variants: the first
 * one whose placeholders the finding's `args` can fill is used, so optional
 * args (Fortnox sends no `company` or `status`) get their own wording. A code
 * without an entry, or with no variant that can be filled, falls back to the
 * backend's English `message`, so an unknown code is never a blank row.
 */

type Variants = string | string[];
type Texts = { sv: Variants; en: Variants };

export const DRY_RUN_FINDING_TEXTS: Record<string, Texts> = {
	// Customers
	CUSTOMER_NOT_FOUND: {
		sv: ["Kunden för underlag {{invoiceNumber}} kunde inte hittas", "Kunden kunde inte hittas"],
		en: [
			"The customer of invoice basis {{invoiceNumber}} could not be found",
			"The customer could not be found",
		],
	},
	CUSTOMER_READ_FAILED: {
		sv: [
			"Kunden {{name}} ({{customerNumber}}) kunde inte läsas från fakturasystemet",
			"Kund {{customerNumber}} kunde inte läsas från fakturasystemet",
		],
		en: [
			"Customer {{name}} ({{customerNumber}}) could not be read from the invoice system",
			"Customer {{customerNumber}} could not be read from the invoice system",
		],
	},
	CUSTOMER_INACTIVE: {
		sv: [
			"Kunden {{name}} ({{customerNumber}}) är inaktiv i fakturasystemet (status {{status}})",
			"Kunden {{name}} ({{customerNumber}}) är inaktiv i fakturasystemet",
			"Kund {{customerNumber}} är inaktiv i fakturasystemet",
		],
		en: [
			"Customer {{name}} ({{customerNumber}}) is inactive in the invoice system (status {{status}})",
			"Customer {{name}} ({{customerNumber}}) is inactive in the invoice system",
			"Customer {{customerNumber}} is inactive in the invoice system",
		],
	},
	CUSTOMER_NUMBER_MISSING: {
		sv: [
			"Kunden {{name}} saknar kundnummer",
			"Kundnumret {{customerNumber}} saknas eller är ogiltigt",
			"Kunden saknar kundnummer",
		],
		en: [
			"Customer {{name}} has no customer number",
			"Customer number {{customerNumber}} is missing or invalid",
			"The customer has no customer number",
		],
	},
	DELIVERY_COUNTRY_INVALID: {
		sv: "Leveranslandet {{country}} är ogiltigt. Ange landets namn (t.ex. Sverige), inte en landskod som SE",
		en: "Delivery country {{country}} is invalid. Use the country name (e.g. Sweden), not a code like SE",
	},

	// Invoice delivery method (Leveranssätt)
	GLN_MISMATCH: {
		sv: [
			"GLN-koden för {{name}} ({{customerNumber}}) skiljer sig: Origo har {{originGln}}, Fortnox har {{fortnoxGln}}. Rätta i Fortnox.",
			"GLN-koden skiljer sig: Origo har {{originGln}}, Fortnox har {{fortnoxGln}}. Rätta i Fortnox.",
		],
		en: [
			"The GLN code of {{name}} ({{customerNumber}}) differs: Origo has {{originGln}}, Fortnox has {{fortnoxGln}}. Correct it in Fortnox.",
			"The GLN code differs: Origo has {{originGln}}, Fortnox has {{fortnoxGln}}. Correct it in Fortnox.",
		],
	},
	E_INVOICE_NOT_ACTIVE: {
		sv: [
			"{{name}} ({{customerNumber}}): e-faktura valt i Origo men ingen aktiv e-fakturakoppling i Fortnox; levereras som e-post/post.",
			"E-faktura valt i Origo men ingen aktiv e-fakturakoppling i Fortnox; levereras som e-post/post.",
		],
		en: [
			"{{name}} ({{customerNumber}}): e-invoice chosen in Origo but no active e-invoice connection in Fortnox; delivered by email/post.",
			"E-invoice chosen in Origo but no active e-invoice connection in Fortnox; delivered by email/post.",
		],
	},
	AUTO_INVOICE_NOT_ACCEPTED: {
		sv: [
			"Visma accepterade inte e-faktura (AutoInvoice) för kunden ({{deliveryMethod}}).",
			"Visma accepterade inte e-faktura (AutoInvoice) för kunden.",
		],
		en: [
			"Visma did not accept e-invoice (AutoInvoice) for the customer ({{deliveryMethod}}).",
			"Visma did not accept e-invoice (AutoInvoice) for the customer.",
		],
	},
	PEPPOL_BUYER_REFERENCE_MISSING: {
		sv: [
			"{{name}} ({{customerNumber}}): e-fakturan saknar både Beställningsnummer och Fakturareferens. Den skickas ändå, men mottagande kommun eller företag kan avvisa eller parkera den. Ange Fakturareferens på kunden eller uppdraget, eller ett Beställningsnummer.",
			"E-fakturan saknar både Beställningsnummer och Fakturareferens. Den skickas ändå, men mottagande kommun eller företag kan avvisa eller parkera den. Ange Fakturareferens på kunden eller uppdraget, eller ett Beställningsnummer.",
		],
		en: [
			"{{name}} ({{customerNumber}}): the e-invoice has neither an order number nor an invoice reference. It is still sent, but the receiving municipality or company may reject or park it. Set an invoice reference on the customer or the job, or an order number.",
			"The e-invoice has neither an order number nor an invoice reference. It is still sent, but the receiving municipality or company may reject or park it. Set an invoice reference on the customer or the job, or an order number.",
		],
	},
	DELIVERY_METHOD_DATA_MISSING: {
		sv: ["Leveranssättet {{deliveryMethod}} saknar {{field}}.", "Leveranssättet saknar uppgifter."],
		en: [
			"The delivery method {{deliveryMethod}} is missing {{field}}.",
			"The delivery method is missing details.",
		],
	},

	// Tenant routing, koddelar and subaccounts (Visma.net)
	TENANT_MAPPING_MISSING: {
		sv: "Dotterbolaget {{subsidiaryId}} är inte kopplat till något bolag i Visma",
		en: "Subsidiary {{subsidiaryId}} is not mapped to a Visma company",
	},
	KODDEL_RESULT_UNIT_MISSING: {
		sv: "Kunden saknar resultatenhet för koddelen",
		en: "The customer has no result unit for the subaccount segment",
	},
	KODDEL_CUSTOMER_NUMBER_INVALID: {
		sv: "Kundnummer {{customerNumber}} kan inte användas som koddel",
		en: "Customer number {{customerNumber}} cannot be used as a subaccount segment",
	},
	SUBACCOUNT_LOOKUP_FAILED: {
		sv: [
			"Underkonto {{subaccount}} kunde inte kontrolleras i {{company}}",
			"Underkonto {{subaccount}} kunde inte kontrolleras",
		],
		en: [
			"Subaccount {{subaccount}} could not be checked in {{company}}",
			"Subaccount {{subaccount}} could not be checked",
		],
	},

	// Invoice numbers and rows
	INVOICE_NUMBER_TAKEN: {
		sv: "Fakturanummer {{invoiceNumber}} används redan i fakturasystemet",
		en: "Invoice number {{invoiceNumber}} is already used in the invoice system",
	},
	INVOICE_HAS_NO_ROWS: {
		sv: ["Underlag {{invoiceNumber}} saknar rader", "Fakturan saknar rader"],
		en: ["Invoice basis {{invoiceNumber}} has no rows", "The invoice has no rows"],
	},

	// Articles
	ARTICLE_NOT_FOUND: {
		sv: [
			"Artikel {{articleNumber}} finns inte i {{company}}",
			"Artikel {{articleNumber}} finns inte i fakturasystemet",
		],
		en: [
			"Article {{articleNumber}} does not exist in {{company}}",
			"Article {{articleNumber}} does not exist in the invoice system",
		],
	},
	ARTICLE_NOT_SELLABLE: {
		sv: [
			"Artikel {{articleNumber}} i {{company}} är inte säljbar (status {{status}})",
			"Artikel {{articleNumber}} i {{company}} är inte säljbar",
			"Artikel {{articleNumber}} är inte säljbar",
		],
		en: [
			"Article {{articleNumber}} in {{company}} is not sellable (status {{status}})",
			"Article {{articleNumber}} in {{company}} is not sellable",
			"Article {{articleNumber}} is not sellable",
		],
	},
	ARTICLE_LOOKUP_FAILED: {
		sv: [
			"Artikel {{articleNumber}} kunde inte kontrolleras i {{company}}",
			"Artikel {{articleNumber}} kunde inte kontrolleras",
		],
		en: [
			"Article {{articleNumber}} could not be checked in {{company}}",
			"Article {{articleNumber}} could not be checked",
		],
	},

	// Accounts and VAT
	ACCOUNT_NUMBER_NOT_NUMERIC: {
		sv: "Kontonummer {{accountNumber}} är inte ett nummer",
		en: "Account number {{accountNumber}} is not a number",
	},
	ACCOUNT_NOT_FOUND: {
		sv: [
			"Konto {{accountNumber}} finns inte i {{company}}",
			"Konto {{accountNumber}} finns inte i fakturasystemet",
		],
		en: [
			"Account {{accountNumber}} does not exist in {{company}}",
			"Account {{accountNumber}} does not exist in the invoice system",
		],
	},
	ACCOUNT_INACTIVE: {
		sv: [
			"Konto {{accountNumber}} är inaktivt i {{company}}",
			"Konto {{accountNumber}} är inaktivt i fakturasystemet",
		],
		en: [
			"Account {{accountNumber}} is inactive in {{company}}",
			"Account {{accountNumber}} is inactive in the invoice system",
		],
	},
	VAT_CODE_NOT_FOUND: {
		sv: [
			"Momskod {{vatCodeId}} finns inte i {{company}}",
			"Momskod {{vatCodeId}} finns inte i fakturasystemet",
		],
		en: [
			"VAT code {{vatCodeId}} does not exist in {{company}}",
			"VAT code {{vatCodeId}} does not exist in the invoice system",
		],
	},

	// Financial period
	FINANCIAL_PERIOD_NOT_FOUND: {
		sv: [
			"Räkenskapsperiod {{period}} (fakturadatum {{documentDate}}) finns inte i {{company}}",
			"Räkenskapsperiod {{period}} (fakturadatum {{documentDate}}) finns inte",
		],
		en: [
			"Financial period {{period}} (invoice date {{documentDate}}) does not exist in {{company}}",
			"Financial period {{period}} (invoice date {{documentDate}}) does not exist",
		],
	},
	FINANCIAL_PERIOD_CLOSED: {
		sv: [
			"Räkenskapsperiod {{period}} (fakturadatum {{documentDate}}) är stängd i {{company}}",
			"Räkenskapsperiod {{period}} (fakturadatum {{documentDate}}) är stängd",
		],
		en: [
			"Financial period {{period}} (invoice date {{documentDate}}) is closed in {{company}}",
			"Financial period {{period}} (invoice date {{documentDate}}) is closed",
		],
	},

	// Branch, payment method, salesperson (Visma.net)
	BRANCH_NOT_FOUND: {
		sv: ["Filial {{branchNumber}} finns inte i {{company}}", "Filial {{branchNumber}} finns inte"],
		en: [
			"Branch {{branchNumber}} does not exist in {{company}}",
			"Branch {{branchNumber}} does not exist",
		],
	},
	BRANCH_INACTIVE: {
		sv: ["Filial {{branchNumber}} är inaktiv i {{company}}", "Filial {{branchNumber}} är inaktiv"],
		en: [
			"Branch {{branchNumber}} is inactive in {{company}}",
			"Branch {{branchNumber}} is inactive",
		],
	},
	PAYMENT_METHOD_NOT_FOUND: {
		sv: [
			"Betalningssätt {{paymentMethodId}} finns inte i {{company}}",
			"Betalningssätt {{paymentMethodId}} finns inte",
		],
		en: [
			"Payment method {{paymentMethodId}} does not exist in {{company}}",
			"Payment method {{paymentMethodId}} does not exist",
		],
	},
	PAYMENT_METHOD_INACTIVE: {
		sv: [
			"Betalningssätt {{paymentMethodId}} är inaktivt i {{company}}",
			"Betalningssätt {{paymentMethodId}} är inaktivt",
		],
		en: [
			"Payment method {{paymentMethodId}} is inactive in {{company}}",
			"Payment method {{paymentMethodId}} is inactive",
		],
	},
	SALESPERSON_NOT_FOUND: {
		sv: ["Säljare {{salesperson}} finns inte i {{company}}", "Säljare {{salesperson}} finns inte"],
		en: [
			"Salesperson {{salesperson}} does not exist in {{company}}",
			"Salesperson {{salesperson}} does not exist",
		],
	},
	SALESPERSON_INACTIVE: {
		sv: ["Säljare {{salesperson}} är inaktiv i {{company}}", "Säljare {{salesperson}} är inaktiv"],
		en: [
			"Salesperson {{salesperson}} is inactive in {{company}}",
			"Salesperson {{salesperson}} is inactive",
		],
	},

	// Specification PDF (Visma.net)
	SPECIFICATION_INVALID: {
		sv: ["Specifikationen är ogiltig: {{error}}", "Specifikationen är ogiltig"],
		en: ["The specification is invalid: {{error}}", "The specification is invalid"],
	},
	SPECIFICATION_RENDER_FAILED: {
		sv: [
			"Specifikationen (PDF) kunde inte skapas: {{error}}",
			"Specifikationen (PDF) kunde inte skapas",
		],
		en: [
			"The specification (PDF) could not be rendered: {{error}}",
			"The specification (PDF) could not be rendered",
		],
	},
	SPECIFICATION_EMPTY: {
		sv: "Specifikationen är tom",
		en: "The specification is empty",
	},

	// Peppol references from the invoice template (Visma.net)
	EXPORT_REFERENCES_INVALID: {
		sv: [
			"Fakturamallen gav ogiltiga referenser (exportData.references) för underlag {{invoiceNumber}}: {{error}}. Inget skrivs till Visma.net förrän fakturamallen är rättad.",
			"Fakturamallen gav ogiltiga referenser (exportData.references): {{error}}. Inget skrivs till Visma.net förrän fakturamallen är rättad.",
			"Fakturamallen gav ogiltiga referenser (exportData.references). Inget skrivs till Visma.net förrän fakturamallen är rättad.",
		],
		en: [
			"The invoice template returned invalid references (exportData.references) for invoice basis {{invoiceNumber}}: {{error}}. Nothing is written to Visma.net until the invoice template is fixed.",
			"The invoice template returned invalid references (exportData.references): {{error}}. Nothing is written to Visma.net until the invoice template is fixed.",
			"The invoice template returned invalid references (exportData.references). Nothing is written to Visma.net until the invoice template is fixed.",
		],
	},
};

/** Texts for the codes in `CheckReport.notChecked`. */
export const DRY_RUN_NOT_CHECKED_TEXTS: Record<string, Texts> = {
	ACCOUNTS: {
		sv: "Att kontona finns i bokföringen",
		en: "That the accounts exist in the ledger",
	},
	LOCKED_PERIOD: {
		sv: "Att perioden för fakturadatumet är öppen",
		en: "That the period of the invoice date is open",
	},
	SYSTEM_VALIDATION: {
		sv: "Fakturasystemets egen validering, som görs först när fakturorna skapas",
		en: "The invoice system's own validation, which only runs when the invoices are created",
	},
	AUTOINVOICE_DELIVERY: {
		sv: "Leverans av e-faktura (AutoInvoice)",
		en: "E-invoice delivery (AutoInvoice)",
	},
	ATTACHMENT_UPLOAD: {
		sv: "Uppladdning av specifikationen (PDF) till fakturan",
		en: "Uploading the specification (PDF) to the invoice",
	},
	INVOICE_NUMBERS: {
		sv: "Att fakturanumren är lediga",
		en: "That the invoice numbers are free",
	},
	BRANCHES: {
		sv: "Att filialerna finns och är aktiva",
		en: "That the branches exist and are active",
	},
	PAYMENT_METHODS: {
		sv: "Att betalningssätten finns och är aktiva",
		en: "That the payment methods exist and are active",
	},
	VAT_CODES: {
		sv: "Att momskoderna finns",
		en: "That the VAT codes exist",
	},
	SALESPERSONS: {
		sv: "Att säljarna finns och är aktiva",
		en: "That the salespersons exist and are active",
	},
};

/** `sv` for Swedish UI languages ("sv", "sv-SE"), else `en` (the app's fallback language). */
export function textLanguage(language?: string): keyof Texts {
	return language?.toLowerCase().startsWith("sv") ? "sv" : "en";
}

/**
 * Replaces `{{name}}` with `args.name`. Undefined when a placeholder has no
 * value, so the caller can try another variant or fall back instead of
 * showing a half-filled text.
 */
export function interpolate(
	template: string,
	args: Record<string, string | number> = {}
): string | undefined {
	let missing = false;

	const text = template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name: string) => {
		const value = args[name];

		if (value === undefined || value === null || value === "") {
			missing = true;
			return "";
		}

		return String(value);
	});

	return missing ? undefined : text;
}

/** The first variant whose placeholders `args` can all fill. */
export function interpolateFirst(
	variants: Variants,
	args?: Record<string, string | number>
): string | undefined {
	for (const variant of Array.isArray(variants) ? variants : [variants]) {
		const text = interpolate(variant, args);
		if (text) return text;
	}

	return undefined;
}

/** Delivery method enum labels, for the `deliveryMethod` arg (same wording as the locale's `deliveryMethod.label`). */
const DELIVERY_METHOD_LABELS: Record<string, Texts> = {
	E_INVOICE_GLN: { sv: "E-faktura (GLN)", en: "E-invoice (GLN)" },
	E_INVOICE_ORG: { sv: "E-faktura (org.nr)", en: "E-invoice (org. no.)" },
	EMAIL: { sv: "E-post", en: "Email" },
	PRINT: { sv: "Utskrift", en: "Print" },
};

/** Customer field names a delivery finding may name in its `field` arg. */
const DELIVERY_FIELD_LABELS: Record<string, Texts> = {
	GLNCode: { sv: "GLN-kod", en: "GLN code" },
	gln: { sv: "GLN-kod", en: "GLN code" },
	vatNumber: { sv: "org.nr", en: "org. no." },
	corporateId: { sv: "org.nr", en: "org. no." },
	email: { sv: "e-postadress", en: "email address" },
};

/** Enum codes in `args` (deliveryMethod, field) replaced by their display labels; unknown values pass through. */
function displayArgs(
	args: Record<string, string | number> | undefined,
	lang: keyof Texts
): Record<string, string | number> | undefined {
	if (!args) return args;

	const label = (labels: Record<string, Texts>, value: string | number | undefined) =>
		typeof value === "string" && labels[value] ? String(labels[value][lang]) : value;

	return {
		...args,
		...(args.deliveryMethod !== undefined && {
			deliveryMethod: label(DELIVERY_METHOD_LABELS, args.deliveryMethod)!,
		}),
		...(args.field !== undefined && { field: label(DELIVERY_FIELD_LABELS, args.field)! }),
	};
}

/** Text of a finding in the UI language, falling back to the backend's `message`. */
export function translateDryRunFinding(
	finding: { code: string; message: string; args?: Record<string, string | number> },
	language?: string
): string {
	const lang = textLanguage(language);
	const texts = DRY_RUN_FINDING_TEXTS[finding.code];
	const text = texts && interpolateFirst(texts[lang], displayArgs(finding.args, lang));

	return text || finding.message || finding.code;
}

/** Text of a `notChecked` code in the UI language, falling back to the code itself. */
export function translateDryRunNotChecked(code: string, language?: string): string {
	const texts = DRY_RUN_NOT_CHECKED_TEXTS[code];

	return (texts && interpolateFirst(texts[textLanguage(language)])) ?? code;
}
